import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Theme } from '@material-ui/core';
import { Paper, Grid } from '@material-ui/core';
import { showToast } from '@klpkonsern/ui';

import { ActionButton, Block, Typography, useKlpStyles } from '@klp/design';
import { useUpsertReportMappingMutation } from '@klp/reportMapping/api/rtkApi';
import { ByggSelect } from '@klp/reportMapping/components/Form/ByggSelect';
import { KundeSelect } from '@klp/reportMapping/components/Form/KundeSelect';
import { ReportTypeSelect } from '@klp/reportMapping/components/Form/ReportTypeSelect';
import type { ReportWithMappingDto } from '@klp/reportMapping/types';
import { MeassurementCategoryEnum } from '@klp/reportMapping/types';
import { getTranslationKeys } from './helpers';
import type { Bygg } from '@klp/contextSelector/interfaces';
import type { Kunde } from '@forvalter/types/ForvalterCommonDAL';

type MappingFormProps = {
    reportData: ReportWithMappingDto;
    onClose: () => void;
    initialByggOption: Array<Bygg>;
    initialKundeOption: Kunde | null;
};

const styles = (theme: Theme) => ({
    paper: {
        padding: theme.spacing(2),
    },
    error: { color: theme.palette.error.main },
});

export const MappingForm: React.FC<MappingFormProps> = ({
    reportData,
    onClose,
    initialByggOption,
    initialKundeOption,
}) => {
    const { t } = useTranslation();
    const classes = useKlpStyles({}, styles);
    const isEditMode = reportData.isMapped;

    const [selectedBygg, setSelectedBygg] =
        useState<Array<Bygg>>(initialByggOption);
    const [selectedKunde, setSelectedKunde] = useState<Kunde | null>(
        initialKundeOption ?? null,
    );
    const [selectedReportType, setSelectedReportType] = useState<string>(
        reportData.mapping?.reportType ?? '',
    );

    const [upsertMapping, { isLoading: saveLoading }] =
        useUpsertReportMappingMutation({ fixedCacheKey: 'shared-upsert' });

    const resetForm = () => {
        if (isEditMode) {
            return;
        }
        setSelectedBygg([]);
        setSelectedKunde(null);
        setSelectedReportType('');
    };

    const handleSaveSuccess = () => {
        showToast({ body: t('general.saved'), type: 'success' });
        resetForm();
        onClose();
    };

    const handleSaveError = (errorMessage?: string) => {
        showToast({
            body: t(errorMessage ?? 'general.error.message'),
            type: 'error',
        });
    };

    const handleSave = async () => {
        // Bygg is always mandatory
        // Kunde is mandatory only for CustomerSpecificEnergy
        const isKundeRequired =
            selectedReportType ===
            MeassurementCategoryEnum.CustomerSpecificEnergy;

        if (
            !selectedReportType ||
            !selectedBygg ||
            (isKundeRequired && !selectedKunde)
        ) {
            return;
        }

        try {
            await upsertMapping({
                reportId: reportData.id,
                byggNummer: selectedBygg.map((bygg) => bygg.nummer).join(','),
                kundenummer: selectedKunde?.nummer ?? '',
                reportType: selectedReportType,
                externalId: null,
            }).unwrap();

            handleSaveSuccess();
        } catch (upsertError) {
            handleSaveError('reportMapping.error.saveFailed');
        }
    };

    const { titleTranslationKey, saveTranslationKey } =
        getTranslationKeys(isEditMode);

    return (
        <Paper className={classes.paper} elevation={2}>
            <Typography variant="subtitle1" translation={titleTranslationKey} />
            <Typography
                variant="subtitle1"
                marginBottom="compact"
                klpPalette="secondary"
                klpColor="dark"
            >
                {reportData.title}
            </Typography>
            <Grid container={true} spacing={2}>
                <Grid item={true} xs={12}>
                    <ReportTypeSelect
                        value={selectedReportType}
                        onChange={setSelectedReportType}
                    />
                </Grid>
                <Grid item={true} xs={12}>
                    <ByggSelect
                        selectedBuilding={selectedBygg}
                        onChange={setSelectedBygg}
                        required={true}
                    />
                </Grid>

                {selectedReportType ===
                    MeassurementCategoryEnum.CustomerSpecificEnergy && (
                    <Grid item={true} xs={12}>
                        <KundeSelect
                            selectedKunde={selectedKunde}
                            onChange={setSelectedKunde}
                            required={true}
                        />
                    </Grid>
                )}
            </Grid>

            <Grid
                container={true}
                spacing={2}
                alignItems="center"
                justifyContent="flex-end"
            >
                <Grid item={true}>
                    <Block marginTop="tiny">
                        <ActionButton
                            actionType="submit"
                            variant="contained"
                            color="secondary"
                            onClick={handleSave}
                            disabled={
                                !selectedReportType ||
                                selectedBygg.length === 0 ||
                                (selectedReportType ===
                                    MeassurementCategoryEnum.CustomerSpecificEnergy &&
                                    !selectedKunde)
                            }
                            isLoading={saveLoading}
                            translation={saveTranslationKey}
                        />
                    </Block>
                </Grid>
            </Grid>
        </Paper>
    );
};
